import { getTabToken } from "common/MetaActions";
import config from "config/config";
import { Attachment } from "models/dashboard/dashboard";
import { useRootStore } from "store";
import { MessageType } from "store/slices/inboxSlice";
import request from "superagent";

type QueryParams = { skip: number; limit: number; searchCriteria?: string };

export class InboxAPI {
  static fetchThread = async (customerId: string, threadId: string) => {
    const response = await request
      .get(
        config.api.customer.thread.get +
          customerId +
          "/thread/" +
          encodeURIComponent(threadId)
      )
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());

    return response.body;
  };

  static fetchThreads = async (
    customerId: string,
    getAllThreads = false,
    skip = 0,
    searchTerms = "",
    queryParams?: string
  ) => {
    let queryParam: QueryParams = {
      skip: skip,
      limit: config.defaults.pageSize
    };
    if (searchTerms !== "") {
      queryParam = { ...queryParam, searchCriteria: searchTerms };
    }

    if (queryParams) {
      const urlParams = new URLSearchParams(queryParams);
      urlParams.forEach((value, key) => {
        queryParam[key] = value;
      });
    }

    const response = await request
      .get(
        config.api.customer.threads.get +
          customerId +
          "/threads" +
          (getAllThreads ? "/All" : "")
      )
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .query(queryParam);

    return response.body;
  };

  static fetchMoreThreads = async (
    customerId: string,
    getAllThreads: boolean,
    skip: number,
    searchTerms?: string,
    queryParams?: string
  ) => {
    let queryParam: QueryParams = {
      skip: skip,
      limit: config.defaults.pageSize
    };
    if (searchTerms !== "") {
      queryParam = { ...queryParam, searchCriteria: searchTerms };
    }

    const response = await request
      .get(
        config.api.customer.threads.get +
          customerId +
          "/threads" +
          (getAllThreads ? "/All" : "") +
          (queryParams ? `?${queryParams}` : "")
      )
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .query(queryParam);

    return response.body;
  };

  static setThreadStatus = async (threadId: string, action: string) => {
    const workingCustomerId = useRootStore.getState().global.workingCustomerId;

    const response = await request
      .put(config.api.customer.thread.put(workingCustomerId, threadId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .send({
        Action: action
      });

    return response;
  };

  static fetchSentMessages = async (customerId: string) => {
    const response = await request
      .get(config.api.message.sentMessage(customerId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());

    return response.body;
  };

  static sendMessage = async (
    fromUserId: string,
    body: string,
    title: string,
    threadId: string | null,
    subscribers: string[],
    customerId: string,
    attachments: Attachment[],
    type = MessageType.Normal,
    reportStart = "",
    reportEnd = "",
    category?: number
  ) => {
    const response = await request
      .post(config.api.message.post)
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .send({
        From: fromUserId,
        Body: body,
        Title: title,
        ThreadId: threadId,
        Subscribers: subscribers,
        CustomerId: customerId,
        Attachments: attachments,
        Type: type,
        ReportStartPeriod: reportStart,
        ReportEndPeriod: reportEnd,
        ThreadCategory: category
      });

    return response.body;
  };

  static archiveThread = async (customerId: string, threadId: string) => {
    const response = await request
      .put(config.api.messages.archive(customerId, threadId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());
    return response.body;
  };

  static unarchiveThread = async (customerId: string, threadId: string) => {
    const response = await request
      .put(config.api.messages.unarchive(customerId, threadId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());
    return response.body;
  };

  static hideThreadMessage = async (customerId: string, messageId: string) => {
    const response = await request
      .put(config.api.message.hide(customerId, messageId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());

    return response.body;
  };

  static unhideThreadMessage = async (
    customerId: string,
    messageId: string
  ) => {
    const response = await request
      .put(config.api.message.unhide(customerId, messageId))
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken());

    return response.body;
  };

  static updateThreadCategory = async (
    customerId: string,
    threadId: string,
    category: number
  ) => {
    const response = await request
      .put(
        `${config.api.customer.thread.get}${customerId}/thread/${encodeURIComponent(
          threadId
        )}/category`
      )
      .set("Accept", "application/json")
      .set("Authorization", "Bearer " + getTabToken())
      .send({
        Category: category
      });

    return response.body;
  };
}
